import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePlansDiscover } from '../hooks/usePlans';
import { useChat } from '../hooks/useChat';
import ChatOverlay from '../components/chat/ChatOverlay';
import PlanChatOverlay from '../components/chat/PlanChatOverlay';
import {
  planChatEntryStorageKey, usePlanChat, type PlanChatEntry,
} from '../hooks/usePlanChat';
import HeroSearch from '../components/plans-discover/HeroSearch';
import FeaturedPlanCard from '../components/plans-discover/FeaturedPlanCard';
import PlanLeaderboard from '../components/plans-discover/PlanLeaderboard';
import { TimingSignalStrip, MatchMeCard, IntelEmailCapture } from '../components/plans';
import CreatorMiniPanel from '../components/plans/CreatorMiniPanel';
import ForkModal from '../components/plans/ForkModal';
import ForkGateModal, { storeForkIntent } from '../components/plans/ForkGateModal';
import ShareSheet from '../components/plans/ShareSheet';
import { UnsplashCredit } from '../components/common/UnsplashCredit';
import { HeroImage } from '../components/common';
import { BottomNav } from '../components/navigation';
import { useUserStore } from '../stores';
import { usePlansStore } from '../stores/usePlansStore';
import { trackPlanForked } from '../hooks/useAnalytics';
import type { MatchMeAnswers } from '../components/plans/MatchMeCard';
import type { CommunityPlanListItem, DestinationIntel } from '../types';
import { resolveExploreTarget } from '../utils/exploreTarget';
import { newUuid } from '../utils/uuid';
import './PlansV2.css';
import {
  destinationHref, fetchExploreFrame, type ExploreFilters, type ExploreFrame,
} from '../services/exploreApi';
import { MapSurface } from '../components/explore/MapSurface';

const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`;

/** One "More plans" card. Its own component so each card knows whether its
 *  image is the paper fallback (no photograph, or one that failed to load),
 *  as HeroImage reports it. Bare, the card paints no scrim and its title and
 *  meta go ink (decision 25, r1123 finding 1). */
const MoreCard: React.FC<{ plan: CommunityPlanListItem; onClick: () => void }> = ({ plan: p, onClick }) => {
  const [bare, setBare] = useState(!p.hero_thumb_url);
  return (
    <button className={`pv2-more-card${bare ? ' pv2-more-card--bare' : ''}`} onClick={onClick}>
      <div className="pv2-more-card__img">
        <HeroImage src={p.hero_thumb_url} fallbackKey={p.title} size="hero" onBareChange={setBare} showLabel={false} />
        {!bare && <div className="pv2-more-card__overlay" />}
      </div>
      <div className="pv2-more-card__body">
        <span className="pv2-more-card__title">{p.title}</span>
        <span className="pv2-more-card__meta">
          {[
            `${p.duration_days} days`,
            p.used_count > 0 ? `${fmt(p.used_count)} used` : '',
          ].filter(Boolean).join(' · ')}
        </span>
      </div>
    </button>
  );
};

const PlansV2: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const discover = usePlansDiscover();
  const [matchMeCompleted, setMatchMeCompleted] = useState(() => !!localStorage.getItem('vizday_matchme_done'));
  const [chatAutoOpened, setChatAutoOpened] = useState(false);

  const chat = useChat({
    onNavigate: (path) => navigate(path),
    onDataRefresh: () => discover.refresh(),
    context: 'discovery',
  });

  const [restoredPlanChatEntry] = useState<PlanChatEntry | null>(() => {
    try {
      const raw = sessionStorage.getItem(
        planChatEntryStorageKey(useUserStore.getState().user?.id ?? null),
      );
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PlanChatEntry;
      return parsed.intent === 'new_plan'
        && typeof parsed.entryId === 'string'
        && (parsed.query === undefined || typeof parsed.query === 'string')
        ? parsed : null;
    } catch { return null; }
  });
  const planChat = usePlanChat(null, 'plan_create', null, restoredPlanChatEntry);
  const [isPlanChatOpen, setIsPlanChatOpen] = useState(!!restoredPlanChatEntry);
  const [creatorPanel, setCreatorPanel] = useState<{ name: string; rect: DOMRect | null } | null>(null);
  const [forkTarget, setForkTarget] = useState<CommunityPlanListItem | null>(null);
  const [showForkModal, setShowForkModal] = useState(false);
  const [showForkGate, setShowForkGate] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState<number | null>(null);
  const user = useUserStore((s) => s.user);
  const { forkPlan } = usePlansStore();

  const creatorFilter = searchParams.get('creator') || '';

  // Auto-open chat from ?chat=1 redirect, clean URL.
  //
  // Opens the PLAN chat, not the unified one. /chat redirects here without a
  // user check, and the unified endpoint is authenticated — so a public
  // visitor arriving this way met a chat window that failed on every send.
  // The plan chat is the anonymous funnel and works signed out.
  useEffect(() => {
    if (searchParams.get('chat') === '1' && !chatAutoOpened) {
      setChatAutoOpened(true);
      planChat.reset({ intent: 'new_plan', entryId: newUuid() });
      setIsPlanChatOpen(true);
      navigate('/explore', { replace: true });
    }
  }, [searchParams, navigate, planChat, chatAutoOpened]);

  const goToPlan = (slug: string) => navigate(`/plans/p/${slug}`);

  /**
   * A trending row is a destination, so it opens one.
   *
   * It used to substring-match the name against the loaded community plans and
   * — with no else branch — do nothing when none matched, which is the common
   * case: a trending destination usually has no community plan yet. Linked
   * rows now carry `slug`; an unlinked one runs the same resolution the search
   * box runs (atlas, then a community plan, then Ask AI), so the two entries
   * cannot disagree about where a name leads.
   */
  // Two refs, because they answer two different questions. `trendingBusy` is a
  // synchronous latch: the unlinked branch awaits a network round trip, so
  // without it a second click starts a second resolution — two chat resets with
  // different entry ids — and a slow answer to the first click yanks the reader
  // off wherever the second one took them. `mounted` is the late-answer guard:
  // acting on a resolve that lands after the page is gone is a navigation
  // nobody asked for.
  const trendingBusy = useRef(false);
  const mounted = useRef(true);
  // Set on SETUP as well as cleared on cleanup. StrictMode runs setup →
  // cleanup → setup in development, so a cleanup-only ref is left false for the
  // rest of the component's life and every unlinked lookup then returns at the
  // guard below — working in production and dead in every dev build, which is
  // the worst of the two.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const handleTrendingClick = async (item: DestinationIntel) => {
    // An empty name resolves to nothing anyone meant; it is malformed data, not
    // a search.
    if (trendingBusy.current || !item.destination.trim()) return;
    trendingBusy.current = true;
    try {
      if (item.slug) {
        navigate(`/d/${item.slug}`);
        return;
      }
      const target = await resolveExploreTarget(item.destination);
      if (!mounted.current) return;
      if (target.kind === 'destination') navigate(`/d/${target.slug}`);
      else if (target.kind === 'plan') goToPlan(target.slug);
      else {
        planChat.reset({ intent: 'new_plan', query: target.query, entryId: newUuid() });
        setIsPlanChatOpen(true);
      }
    } finally {
      // Ask AI keeps the reader on Explore, so the strip has to work again.
      trendingBusy.current = false;
    }
  };

  // Sort, then split per section. The season/group filtering that lived here
  // went with its pills (slice 4c); the CREATOR filter stays, because it is not
  // a §1.8 view filter — it arrives from profile links, not from a control on
  // this surface, and removing it would break inbound URLs.
  const sorted = useMemo(() => {
    let plans = [...discover.communityPlans];
    if (creatorFilter) {
      plans = plans.filter(p => (p.creator_name || '').toLowerCase() === creatorFilter.toLowerCase());
    }
    return plans.sort((a, b) => b.score - a.score || b.id - a.id);
  }, [discover.communityPlans, creatorFilter]);

  const featured = sorted.slice(0, 2);
  const more = sorted.slice(2);

  const handleMatchMeComplete = (answers: MatchMeAnswers) => {
    localStorage.setItem('vizday_matchme_done', 'true');
    setMatchMeCompleted(true);
    discover.applyFilters({ search: answers.tripTypes?.[0] || '' });
  };

  const handleMatchMeSkip = () => {
    localStorage.setItem('vizday_matchme_done', 'true');
    setMatchMeCompleted(true);
  };

  const handleCardFork = (plan: CommunityPlanListItem) => {
    setForkTarget(plan);
    if (!user) {
      setShowForkGate(true);
    } else {
      setShowForkModal(true);
    }
  };

  const handleForkConfirm = async (startDate: string): Promise<{ id: number }> => {
    if (!forkTarget) throw new Error('No plan');
    const result = await forkPlan(forkTarget.id, startDate);
    trackPlanForked(forkTarget.id, forkTarget.destination);
    return { id: (result as { id: number }).id };
  };

  const handleForkGateSignUp = () => {
    if (!forkTarget) return;
    storeForkIntent(forkTarget.slug);
    setShowForkGate(false);
    navigate('/login', { state: { returnTo: window.location.pathname } });
  };

  /** §1.6's miss path, shared with the inline box: a search that found nothing
   *  ends at Ask AI, wherever it was typed. */
  const openAskAI = useCallback((query: string) => {
    planChat.reset({ intent: 'new_plan', query, entryId: newUuid() });
    setIsPlanChatOpen(true);
  }, [planChat]);

  /** §1.3's opening frame — where the map should open.
   *
   *  Fetched here rather than inside the map, because it is a fact the page
   *  supplies, like the destination handler beside it. `undefined` means "still
   *  asking", `'failed'` means the surface should open on its own default and
   *  say nothing about a home: a frame nobody could read is not evidence that
   *  the reader has none. */
  const [frame, setFrame] = useState<ExploreFrame | 'failed' | undefined>();
  useEffect(() => {
    let live = true;
    fetchExploreFrame()
      .then((f) => { if (live) setFrame(f); })
      .catch(() => { if (live) setFrame('failed'); });
    return () => { live = false; };
  }, []);

  /** §1.8: "tapping through to `/d/<slug>` carries the active filters, so the
   *  page's plan list shows the matching plans" — the pin's number and the page
   *  a reader lands on never disagree. An unfiltered tap carries nothing, so
   *  the ordinary address stays exactly as it was. */
  const openDestination = useCallback((slug: string, filters: ExploreFilters) => {
    navigate(destinationHref(slug, filters, { jumpToSolePlan: true }));
  }, [navigate]);

  // ONE MapSurface, at ONE position in the tree.
  //
  // Four separate mounts — three as a direct child, one nested inside
  // `.pv2-content` — meant React reconciled them as DIFFERENT elements, so the
  // ordinary loading-to-loaded transition unmounted the map and mounted
  // another: viewport, cards and cursor all reset, and a second request issued
  // to a rate-limited endpoint because the first had already gone out and
  // could not be recalled. Hoisting it above the branch keeps it the same
  // element across every transition, which is also why it no longer needs to
  // escape `.pv2-content`'s reading-width cap: it is not inside it.
  const legacyBody = (() => {
    if (discover.isLoadingPlans && discover.communityPlans.length === 0) {
      // The map does not wait for the community list. They are separate
      // requests to separate endpoints, and gating one on the other made a
      // slow legacy call hide a surface that was ready.
      return <div className="pv2-loading">Loading plans...</div>;
    }
    if (discover.error) {
      // Nor does it disappear when the community list FAILS. The Explore
      // endpoint is independent and was answering perfectly while this branch
      // rendered a bare error page — observed in a browser, where the whole map
      // was replaced by "Failed to fetch" from an unrelated call. The map's own
      // failures are reported inside the map.
      return (
        <div className="pv2-error">
          <p>{discover.error}</p>
          <button className="pv2-retry-btn" onClick={discover.refresh}>Try again</button>
        </div>
      );
    }
    if (discover.communityPlans.length === 0) {
      // The atlas has guides whether or not anyone has published a plan, so
      // "No plans found" beside a populated map is a truer screen than an
      // empty one.
      return (
        <div className="pv2-empty">
          <p className="pv2-empty-title">No plans found</p>
          <p className="pv2-empty-body">Check back soon — new plans are added regularly.</p>
        </div>
      );
    }
    return null;
  })();

  if (legacyBody) {
    return (
      <div className="pv2-page">
        <MapSurface onOpen={openDestination} frame={frame}
          onOpenPlan={goToPlan} onAskAI={openAskAI} />
        {legacyBody}
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  return (
    <div className="pv2-page">
      {/* The Explore map (§1.1), at the SAME position as in the branch above so
          React keeps it mounted across the transition. It sits over the
          existing community list rather than replacing it: moving the
          client-filtered list onto the server-side search endpoint is slice 4c,
          and doing it here would change a behaviour readers rely on inside a
          slice that only adds. */}
      <MapSurface onOpen={openDestination} frame={frame}
        onOpenPlan={goToPlan} onAskAI={openAskAI} />
      <div className="pv2-content">
        {/* Creator filter banner */}
        {creatorFilter && (
          <div className="pv2-creator-filter-banner">
            <span>Plans by <strong>{creatorFilter}</strong></span>
            <button onClick={() => navigate('/explore', { replace: true })}>Clear ×</button>
          </div>
        )}

        {/* Hero search */}
        {/* No `plans` prop since slice 4c: the box searches the SERVER, so it
            finds every published plan rather than the ones this page happened
            to have loaded. */}
        <HeroSearch
          onPlanClick={goToPlan}
          onDestinationClick={(slug) => navigate(`/d/${slug}`)}
          onAskAI={(query) => {
            planChat.reset({
              intent: 'new_plan',
              query,
              entryId: newUuid(),
            });
            setIsPlanChatOpen(true);
          }}
        />

        {/* The season/group pills are GONE (slice 4c). §1.8: "the chip row
            over the map holds the ONLY view filters" — and since the chips
            arrived this page carried two controls reading "Friends", one
            filtering this list in the browser and one filtering the map on the
            server, wearing the same word for different sets. The chips filter
            the map AND travel to the destination page, which the pills never
            did. What the pills alone offered was SEASON, which is not a chip
            dimension because §1.8 limits the chips to "the only fields plans
            can answer truthfully today" — a recorded regression, accepted on
            the spec's own argument, not a gap nobody noticed. */}

        {/* Match Me */}
        {!matchMeCompleted && (
          <div className="pv2-section-pad">
            <MatchMeCard onComplete={handleMatchMeComplete} onSkip={handleMatchMeSkip} />
          </div>
        )}
        {/* `MatchMeQuiz` is GONE with the pills (round 528): its expandable
            panel was group/season/sort chips driving a server refetch of this
            legacy list — the duplicate-filter defect wearing a slider icon.
            `MatchMeCard` above stays: a one-time About-you quiz whose answers
            seed a single search, which §1.8 keeps "physically apart" from
            standing view filters. */}

        {/* Timing signals */}
        <TimingSignalStrip
          items={discover.trending}
          label="Trending Now"
          onDestinationClick={handleTrendingClick}
        />

        {/* Featured plans (top 2 large cards) */}
        <div className="pv2-section-pad">
          <h3 className="pv2-section-label">Plans that work</h3>
          {featured.map(p => (
            <FeaturedPlanCard key={p.id} plan={p} onClick={() => goToPlan(p.slug)} onCreatorTap={(name, rect) => setCreatorPanel({ name, rect })} onFork={() => handleCardFork(p)} />
          ))}
        </div>

        {/* More plans (horizontal scroll) */}
        {more.length > 0 && (
          <div className="pv2-more-section">
            <h3 className="pv2-section-label pv2-section-pad">More plans</h3>
            <div className="pv2-more-scroll">
              {more.map(p => (
                <div key={p.id} className="pv2-more-frame">
                <MoreCard plan={p} onClick={() => goToPlan(p.slug)} />
                  {/* Outside the button: the photographer must be reachable. */}
                  {p.hero_thumb_url && p.hero_attribution && (
                    <UnsplashCredit
                      photographer={p.hero_attribution.photographer}
                      profileLink={p.hero_attribution.unsplash_link}
                      className="card-photo-credit"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leaderboard */}
        <PlanLeaderboard plans={sorted} onPlanClick={goToPlan} />

        {/* Email capture */}
        <div className="pv2-section-pad pv2-email-section">
          <IntelEmailCapture />
        </div>
      </div>
      <ChatOverlay chat={chat} onNavigate={(path) => navigate(path)} />
      <PlanChatOverlay
        chat={planChat}
        isExpanded={isPlanChatOpen}
        onClose={() => {
          setIsPlanChatOpen(false);
          planChat.reset();
        }}
        onNavigate={(path) => navigate(path)}
      />
      {creatorPanel && (
        <CreatorMiniPanel
          creatorName={creatorPanel.name}
          anchorRect={creatorPanel.rect}
          onClose={() => setCreatorPanel(null)}
        />
      )}

      {/* Fork modals (triggered from card Fork buttons) */}
      {showForkModal && forkTarget && (
        <ForkModal
          planTitle={forkTarget.title}
          destination={forkTarget.destination}
          creatorName={forkTarget.creator_name || 'VizDay'}
          durationDays={forkTarget.duration_days}
          onConfirm={handleForkConfirm}
          onGoToPlan={(id) => navigate(`/itinerary/${id}?chat=1`)}
          onSharePlan={(id) => {
            setShowForkModal(false);
            setShowShareSheet(id);
          }}
          onClose={() => { setShowForkModal(false); setForkTarget(null); }}
        />
      )}
      {showForkGate && forkTarget && (
        <ForkGateModal
          planTitle={forkTarget.title}
          creatorName={forkTarget.creator_name || 'VizDay'}
          destination={forkTarget.destination}
          onSignUp={handleForkGateSignUp}
          onClose={() => { setShowForkGate(false); setForkTarget(null); }}
        />
      )}
      {showShareSheet && (
        <ShareSheet
          itineraryId={showShareSheet}
          title={forkTarget?.title || ''}
          destination={forkTarget?.destination || ''}
          currentVisibility="private"
          onClose={() => {
            const id = showShareSheet;
            setShowShareSheet(null);
            setForkTarget(null);
            navigate(`/itinerary/${id}`);
          }}
        />
      )}

      <BottomNav activeItem="explore" />
    </div>
  );
};

export default PlansV2;
